/**
 * WebSocket notifications for the frontend
 * Handles real-time notifications for admin and BDA interfaces.
 */

import EventEmitter from 'events';
import { config } from '../config';

// Admin Notifications WebSocket
export class AdminNotificationsSocket extends EventEmitter {
  constructor(token) {
    super();
    if (!token) {
      throw new Error('Token is required for WebSocket connection');
    }
    this.token = token;
    this.ws = null;
    this.isConnected = false;
    this.connectionTimeout = null;
    this.readTimeout = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.pingInterval = null;
    this.pongTimeout = null;
    this.lastPongTime = null;
    this.instanceId = Math.random().toString(36).substring(7);
    console.log(`[WebSocket ${this.instanceId}] Instance created with token available:`, !!token);
  }

  connect() {
    if (!this.token) {
      console.error(`[WebSocket ${this.instanceId}] Cannot connect without token`);
      this.emit('error', { 
        type: 'connection', 
        error: new Error('No token available'),
        message: 'Authentication token is required'
      });
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log(`[WebSocket ${this.instanceId}] Already connected`);
      return;
    }

    console.log(`[WebSocket ${this.instanceId}] Attempting to connect`);
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${config.backend.ws.host}${config.backend.ws.endpoints.admin}`;

    try {
      // Remove 'Bearer ' prefix if present and ensure token is clean
      const cleanToken = this.token.replace(/^Bearer\s+/i, '').trim();
      if (!cleanToken) {
        throw new Error('Invalid token format');
      }

      const wsUrlWithToken = `${wsUrl}?token=${encodeURIComponent(cleanToken)}`;
      console.log(`[WebSocket ${this.instanceId}] Connecting to:`, wsUrl);

      // Close existing connection if any
      if (this.ws) {
        console.log(`[WebSocket ${this.instanceId}] Closing existing connection before creating new one`);
        this.disconnect();
      }

      // Random delay between 1 and 3 seconds for initial connection to prevent thundering herd
      const initialDelay = Math.floor(Math.random() * (3000 - 1000 + 1)) + 1000;
      console.log(`[WebSocket ${this.instanceId}] Waiting ${initialDelay/1000} seconds before connecting...`);

      setTimeout(() => {
        console.log(`[WebSocket ${this.instanceId}] Creating new WebSocket instance`);
        this.ws = new WebSocket(wsUrlWithToken);
        this.ws.binaryType = 'arraybuffer';

        // Set connection timeout (5 seconds)
        this.connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            console.log(`[WebSocket ${this.instanceId}] Connection timeout - closing socket`);
            this.ws.close();
            this.emit('error', { 
            type: 'connection', 
            status: 'timeout', 
            message: 'Connection timeout after 5 seconds' 
          });
        }
      }, 5000);

        this.ws.onopen = () => {
          console.log(`[WebSocket ${this.instanceId}] Connected successfully`);
          clearTimeout(this.connectionTimeout);
        this.isConnected = true;
        this.reconnectAttempts = 0;
          this.emit('connected');
        
        // Start ping/pong mechanism
        this.startPingPong();
        
          // Request initial unread notifications
        setTimeout(() => {
            if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
              this.ws.send(JSON.stringify({ 
              type: 'get_unread',
              timestamp: new Date().toISOString()
            }));
          }
        }, 100);
      };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log(`[WebSocket ${this.instanceId}] Received message:`, data);
          
          // Handle pong message
          if (data.type === 'pong') {
            this.handlePong();
            return;
          }
          
          switch (data.type) {
              case 'unread_notifications':
                // First emit the unread_count directly from the message
                if (typeof data.unread_count === 'number') {
                  console.log(`[WebSocket ${this.instanceId}] Emitting unread count:`, data.unread_count);
                  this.emit('unread_count', data.unread_count);
                }
                // Then handle the notifications array
                if (Array.isArray(data.notifications)) {
                  this.emit('unread', data.notifications);
                  // If unread_count wasn't provided, use array length
                  if (typeof data.unread_count !== 'number') {
                    console.log(`[WebSocket ${this.instanceId}] Using notifications length as count:`, data.notifications.length);
                    this.emit('unread_count', data.notifications.length);
                  }
                }
                break;
            case 'notification':
                const notificationData = {
                  ...data.notification,
                  timestamp: new Date().toISOString()
                };
                this.emit('notification', notificationData);
                // Handle unread count update from notification
                if (typeof data.unread_count === 'number') {
                  console.log(`[WebSocket ${this.instanceId}] Updating count from notification:`, data.unread_count);
                  this.emit('unread_count', data.unread_count);
                }
              break;
              case 'mark_read_ack':
              case 'mark_all_read_ack':
                if (data.success) {
                  if (typeof data.count === 'number') {
                    console.log(`[WebSocket ${this.instanceId}] Updating count after mark read:`, data.count);
                    this.emit('unread_count', data.count);
                  }
                  // Request fresh count
                  if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify({ 
                      type: 'get_unread',
                      timestamp: new Date().toISOString()
                    }));
                  }
                }
              break;
            case 'error':
                console.error(`[WebSocket ${this.instanceId}] Server error:`, data.message);
                this.emit('error', {
                type: 'message',
                message: data.message,
                timestamp: new Date().toISOString()
              });
              break;
            default:
                console.log(`[WebSocket ${this.instanceId}] Unknown message type:`, data.type);
          }
        } catch (error) {
            console.error(`[WebSocket ${this.instanceId}] Error parsing message:`, error);
            this.emit('error', {
            type: 'processing',
            error: error.message,
            timestamp: new Date().toISOString()
          });
        }
      };

        this.ws.onclose = (event) => {
          const reason = event.code === 4001 ? 'Authentication failed' :
                        event.code === 4002 ? 'Server error' :
                        event.code === 4003 ? 'User is not authorized' :
                        'Unknown reason';

          console.log(`[WebSocket ${this.instanceId}] Disconnected with code: ${event.code} (${reason})`);
          
          this.isConnected = false;
          this.emit('disconnected');
          this.stopPingPong();

          // Clear timeouts
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
          }

          // Don't reconnect for authentication or permission errors
          if (event.code === 4001 || event.code === 4003) {
            console.error(`[WebSocket ${this.instanceId}] Connection failed: ${reason}`);
            return;
          }

          // Only attempt to reconnect if the connection was closed unexpectedly
          if ((event.code === 1001 || event.code === 1006) && this.reconnectAttempts < this.maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            console.log(`[WebSocket ${this.instanceId}] Scheduling reconnect attempt ${this.reconnectAttempts + 1} in ${delay}ms`);
            setTimeout(() => {
              this.reconnectAttempts++;
              this.connect();
            }, delay);
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log(`[WebSocket ${this.instanceId}] Max reconnection attempts reached`);
            this.emit('error', { 
              type: 'connection', 
              status: 'failed', 
              message: 'Max reconnection attempts reached. Please refresh the page to try again.' 
            });
          }
        };

        this.ws.onerror = (error) => {
          console.error(`[WebSocket ${this.instanceId}] Error:`, error);
          this.emit('error', { type: 'connection', error });
        };

      }, initialDelay);

    } catch (error) {
      console.error(`[WebSocket ${this.instanceId}] Error creating connection:`, error);
      this.emit('error', { 
        type: 'setup', 
        error,
        message: 'Failed to setup WebSocket connection'
      });
    }
  }

  disconnect() {
    console.log(`[WebSocket ${this.instanceId}] Disconnecting`);
    
    this.stopPingPong();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.isConnected = false;
    
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
    }
  }

  // Start ping/pong mechanism
  startPingPong() {
    console.log(`[WebSocket ${this.instanceId}] Starting ping/pong mechanism`);
    this.stopPingPong(); // Clear any existing intervals
    
    // Send ping every 30 seconds
    this.pingInterval = setInterval(() => {
      if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
        console.log(`[WebSocket ${this.instanceId}] Sending ping`);
        this.ws.send(JSON.stringify({ type: 'ping' }));
        
        // Set a timeout for pong response
        this.pongTimeout = setTimeout(() => {
          if (this.lastPongTime && Date.now() - this.lastPongTime > 35000) {
            console.log(`[WebSocket ${this.instanceId}] No pong received, closing connection`);
            this.ws.close();
          }
        }, 5000);
      }
    }, 30000);
  }

  // Stop ping/pong mechanism
  stopPingPong() {
    if (this.pingInterval) {
      console.log(`[WebSocket ${this.instanceId}] Clearing ping interval`);
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      console.log(`[WebSocket ${this.instanceId}] Clearing pong timeout`);
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  // Handle pong response
  handlePong() {
    console.log(`[WebSocket ${this.instanceId}] Received pong`);
    this.lastPongTime = Date.now();
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  markAsRead(notificationId) {
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    
    this.ws.send(JSON.stringify({
      type: 'mark_read',
      notification_id: notificationId
    }));
  }

  markAllAsRead() {
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    
    this.ws.send(JSON.stringify({
      type: 'mark_all_read'
    }));
  }
}

// BDA Notifications WebSocket
export class BDANotificationsSocket extends AdminNotificationsSocket {
  constructor(token) {
    super(token);
    // No need for setupEventHandlers - all event handlers are set in connect()
  }

  connect() {
    if (!this.token) {
      console.error(`[WebSocket ${this.instanceId}] Cannot connect without token`);
      this.emit('error', { 
        type: 'connection', 
        error: new Error('No token available'),
        message: 'Authentication token is required'
      });
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log(`[WebSocket ${this.instanceId}] Already connected`);
      return;
    }

    console.log(`[WebSocket ${this.instanceId}] Attempting to connect`);

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${config.backend.ws.host}${config.backend.ws.endpoints.bda}`;

    try {
      const cleanToken = this.token.replace(/^Bearer\s+/i, '').trim();
      if (!cleanToken) {
        throw new Error('Invalid token format');
      }

      const wsUrlWithToken = `${wsUrl}?token=${encodeURIComponent(cleanToken)}`;
      console.log(`[WebSocket ${this.instanceId}] Connecting to:`, wsUrl);

      if (this.ws) {
        console.log(`[WebSocket ${this.instanceId}] Closing existing connection before creating new one`);
        this.disconnect();
      }

      const initialDelay = Math.floor(Math.random() * (3000 - 1000 + 1)) + 1000;
      console.log(`[WebSocket ${this.instanceId}] Waiting ${initialDelay/1000} seconds before connecting...`);

      setTimeout(() => {
        console.log(`[WebSocket ${this.instanceId}] Creating new WebSocket instance`);
        this.ws = new WebSocket(wsUrlWithToken);
        this.ws.binaryType = 'arraybuffer';

        // Set connection timeout (5 seconds)
        this.connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            console.log(`[WebSocket ${this.instanceId}] Connection timeout - closing socket`);
            this.ws.close();
            this.emit('error', { 
              type: 'connection', 
              status: 'timeout', 
              message: 'Connection timeout after 5 seconds' 
            });
          }
        }, 5000);

        this.ws.onopen = () => {
          console.log(`[WebSocket ${this.instanceId}] Connected successfully`);
          clearTimeout(this.connectionTimeout);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.emit('connected');

          // Start ping/pong mechanism
          this.startPingPong();

          // Request initial unread notifications
          setTimeout(() => {
            if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
              this.ws.send(JSON.stringify({ 
                type: 'get_unread',
                timestamp: new Date().toISOString()
              }));
            }
          }, 100);
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log(`[WebSocket ${this.instanceId}] Received message:`, data);

            // Handle pong message
            if (data.type === 'pong') {
              this.handlePong();
              return;
            }

            switch (data.type) {
              case 'unread_notifications':
                if (Array.isArray(data.notifications)) {
                  this.emit('unread', data.notifications);
                  if (typeof data.unread_count === 'number') {
                    this.emit('unread_count', data.unread_count);
                  } else {
                    this.emit('unread_count', data.notifications.length);
                  }
                }
                break;
              case 'notification':
                this.emit('notification', {
                  ...data.notification,
                  timestamp: new Date().toISOString()
                });
                if (typeof data.unread_count === 'number') {
                  this.emit('unread_count', data.unread_count);
                }
                break;
              case 'mark_read_ack':
              case 'mark_all_read_ack':
                if (data.success) {
                  if (typeof data.count === 'number') {
                    this.emit('unread_count', data.count);
                  }
                  if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify({ 
                      type: 'get_unread',
                      timestamp: new Date().toISOString()
                    }));
                  }
                }
                break;
              case 'error':
                console.error(`[WebSocket ${this.instanceId}] Server error:`, data.message);
                this.emit('error', {
                  type: 'message',
                  message: data.message,
                  timestamp: new Date().toISOString()
                });
                break;
              default:
                console.log(`[WebSocket ${this.instanceId}] Unknown message type:`, data.type);
            }
          } catch (error) {
            console.error(`[WebSocket ${this.instanceId}] Error parsing message:`, error);
            this.emit('error', {
              type: 'processing',
              error: error.message,
              timestamp: new Date().toISOString()
            });
          }
        };

        this.ws.onclose = (event) => {
          const reason = event.code === 4001 ? 'Authentication failed' :
                        event.code === 4002 ? 'Server error' :
                        event.code === 4003 ? 'User is not authorized' :
                        'Unknown reason';

          console.log(`[WebSocket ${this.instanceId}] Disconnected with code: ${event.code} (${reason})`);
          
          this.isConnected = false;
          this.emit('disconnected');
          this.stopPingPong();

          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
          }

          if (event.code === 4001 || event.code === 4003) {
            console.error(`[WebSocket ${this.instanceId}] Connection failed: ${reason}`);
            return;
          }

          if ((event.code === 1001 || event.code === 1006) && this.reconnectAttempts < this.maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            console.log(`[WebSocket ${this.instanceId}] Scheduling reconnect attempt ${this.reconnectAttempts + 1} in ${delay}ms`);
            setTimeout(() => {
              this.reconnectAttempts++;
              this.connect();
            }, delay);
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log(`[WebSocket ${this.instanceId}] Max reconnection attempts reached`);
            this.emit('error', { 
              type: 'connection', 
              status: 'failed', 
              message: 'Max reconnection attempts reached. Please refresh the page to try again.' 
            });
          }
        };

        this.ws.onerror = (error) => {
          console.error(`[WebSocket ${this.instanceId}] Error:`, error);
          this.emit('error', { type: 'connection', error });
        };

      }, initialDelay);

    } catch (error) {
      console.error(`[WebSocket ${this.instanceId}] Error creating connection:`, error);
      this.emit('error', { 
        type: 'setup', 
        error,
        message: 'Failed to setup WebSocket connection'
      });
    }
  }
} 